import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent, useMotionValue, animate } from "framer-motion";
import { IMAGES } from "@/lib/constants";
import { ArrowRight, Sparkles } from "lucide-react";

// ─── Fixed positions for all card slots ───────────────────────────────────
// depth 1 = front, depth 2 = mid, depth 3 = far back
const POSITIONS = [
  // depth 1 — foreground
  { id: 0,  depth: 1, pos: { top: "7%",  left: "52%" }, delay: 0,   dur: 7.5  },
  { id: 1,  depth: 1, pos: { top: "9%",  left: "75%" }, delay: 1.5, dur: 8.5  },
  { id: 2,  depth: 1, pos: { top: "25%", left: "51%" }, delay: 1.8, dur: 9    },
  { id: 3,  depth: 1, pos: { top: "27%", left: "70%" }, delay: 2.2, dur: 9    },
  { id: 4,  depth: 1, pos: { top: "23%", left: "87%" }, delay: 1.1, dur: 8    },
  { id: 5,  depth: 1, pos: { top: "46%", left: "53%" }, delay: 3.2, dur: 8    },
  { id: 6,  depth: 1, pos: { top: "44%", left: "72%" }, delay: 2.8, dur: 9.5  },
  { id: 7,  depth: 1, pos: { top: "49%", left: "88%" }, delay: 0.5, dur: 8    },
  { id: 8,  depth: 1, pos: { top: "66%", left: "69%" }, delay: 3.6, dur: 9    },
  { id: 9,  depth: 1, pos: { top: "65%", left: "51%" }, delay: 1.7, dur: 7.5  },
  { id: 10, depth: 1, pos: { top: "63%", left: "87%" }, delay: 0.3, dur: 8.5  },
  { id: 11, depth: 1, pos: { top: "84%", left: "53%" }, delay: 1.3, dur: 7    },
  { id: 12, depth: 1, pos: { top: "83%", left: "72%" }, delay: 3,   dur: 10   },
  { id: 13, depth: 1, pos: { top: "86%", left: "88%" }, delay: 2.2, dur: 9    },

  // depth 2 — mid
  { id: 14, depth: 2, pos: { top: "4%",  left: "63%" }, delay: 0.6, dur: 11   },
  { id: 15, depth: 2, pos: { top: "15%", left: "57%" }, delay: 2.9, dur: 9.5  },
  { id: 16, depth: 2, pos: { top: "17%", left: "80%" }, delay: 1.4, dur: 8    },
  { id: 17, depth: 2, pos: { top: "36%", left: "60%" }, delay: 3.8, dur: 10   },
  { id: 18, depth: 2, pos: { top: "34%", left: "82%" }, delay: 0.9, dur: 8.5  },
  { id: 19, depth: 2, pos: { top: "56%", left: "63%" }, delay: 2.5, dur: 9    },
  { id: 20, depth: 2, pos: { top: "75%", left: "57%" }, delay: 1.2, dur: 10   },
  { id: 21, depth: 2, pos: { top: "77%", left: "80%" }, delay: 4,   dur: 11   },
  { id: 22, depth: 2, pos: { top: "93%", left: "65%" }, delay: 2,   dur: 8    },

  // depth 3 — far background
  { id: 23, depth: 3, pos: { top: "3%",  left: "83%" }, delay: 1,   dur: 13   },
  { id: 24, depth: 3, pos: { top: "13%", left: "67%" }, delay: 3.3, dur: 12   },
  { id: 25, depth: 3, pos: { top: "32%", left: "51%" }, delay: 0.4, dur: 14   },
  { id: 26, depth: 3, pos: { top: "39%", left: "91%" }, delay: 3.5, dur: 12   },
  { id: 27, depth: 3, pos: { top: "54%", left: "80%" }, delay: 1.6, dur: 13   },
  { id: 28, depth: 3, pos: { top: "60%", left: "93%" }, delay: 4.5, dur: 12   },
  { id: 29, depth: 3, pos: { top: "72%", left: "65%" }, delay: 2.7, dur: 11   },
  { id: 30, depth: 3, pos: { top: "80%", left: "91%" }, delay: 0.8, dur: 13   },
  { id: 31, depth: 3, pos: { top: "91%", left: "79%" }, delay: 3.1, dur: 12   },
];

// ─── 3 card content sets — swap on scroll ─────────────────────────────────
const CARD_SETS = [
  // ═══ SET 0 — Classic Andhra ═══════════════════════════════════════════════
  [
    // depth 1 (ids 0–13)
    { type: "slider",      label: "Spice Level",       value: "Very High",  fill: 90, barColor: "#dc2626" },
    { type: "profile",     label: "Kid-Friendly",      note: "Gentle on young palates",    accent: "#D97706", initial: "K" },
    { type: "base",        label: "Mango Base",         desc: "Rich & tangy raw mango",     dot: "#D97706" },
    { type: "style",       label: "Andhra Style",       desc: "Bold, spicy, oil-rich" },
    { type: "ingredients", tags: ["Mustard Seeds", "Curry Leaves", "Hing"] },
    { type: "slider",      label: "Salt Level",         value: "Low",        fill: 22, barColor: "#f59e0b" },
    { type: "jar",         size: "Family Jar",          weight: "500g",      price: "₹499" },
    { type: "profile",     label: "Heart-Healthy",      note: "Low sodium, smart oils",     accent: "#9F3E18", initial: "H" },
    { type: "slider",      label: "Sourness",           value: "High",       fill: 78, barColor: "#eab308" },
    { type: "base",        label: "Gongura Base",       desc: "Tart Andhra sorrel leaves",  dot: "#7C3AED" },
    { type: "texture",     label: "Chunky",             sub: "Whole rustic pieces" },
    { type: "slider",      label: "Oil Level",          value: "Medium",     fill: 50, barColor: "#f97316" },
    { type: "style",       label: "Tangy & Bright",     desc: "Citrus-forward, zesty" },
    { type: "ingredients", tags: ["Sesame Oil", "Fenugreek", "Ginger"] },
    // depth 2 (ids 14–22)
    { type: "slider",      label: "Spice Level",        value: "Medium",     fill: 55, barColor: "#4A5D23" },
    { type: "profile",     label: "Pregnancy-Safe",     note: "Comfort-first choices",      accent: "#C8A951", initial: "P" },
    { type: "jar",         size: "Trial Jar",           weight: "150g",      price: "₹199" },
    { type: "style",       label: "Telangana Style",    desc: "Rustic, earthy, balanced" },
    { type: "ingredients", tags: ["Whole Chillies", "Jaggery Hint"] },
    { type: "texture",     label: "Fine-cut",           sub: "Delicate & even slices" },
    { type: "base",        label: "Green Chilli Base",  desc: "Fresh, fiery chillies",      dot: "#16A34A" },
    { type: "profile",     label: "Senior-Focused",     note: "Easy to digest, low sodium", accent: "#4A5D23", initial: "S" },
    { type: "slider",      label: "Salt Level",         value: "Very Low",   fill: 10, barColor: "#f59e0b" },
    // depth 3 (ids 23–31)
    { type: "slider",      label: "Spice Level",        value: "Low",        fill: 30, barColor: "#4A5D23" },
    { type: "base",        label: "Tomato Base",        desc: "Ripe, savory tomato",        dot: "#DC2626" },
    { type: "style",       label: "Low-Oil Modern",     desc: "Lighter, health-conscious" },
    { type: "ingredients", tags: ["Amla", "Turmeric", "Mustard Oil"] },
    { type: "texture",     label: "Soft",               sub: "Melt-in-mouth texture" },
    { type: "profile",     label: "Diabetic-Friendly",  note: "Low glycemic choices",       accent: "#2E5C5A", initial: "D" },
    { type: "base",        label: "Amla Base",          desc: "Indian gooseberry, tangy",   dot: "#65A30D" },
    { type: "slider",      label: "Oil Level",          value: "Very Low",   fill: 12, barColor: "#f97316" },
    { type: "style",       label: "Oil-Rich Classic",   desc: "Deeply marinated, rich" },
  ],

  // ═══ SET 1 — Fiery & Bold ═════════════════════════════════════════════════
  [
    // depth 1
    { type: "slider",      label: "Spice Level",        value: "Max Heat",   fill: 98, barColor: "#991b1b" },
    { type: "profile",     label: "Senior-Focused",     note: "Easy on digestion",          accent: "#4A5D23", initial: "S" },
    { type: "base",        label: "Green Chilli Base",  desc: "Fresh, fiery chillies",      dot: "#16A34A" },
    { type: "style",       label: "Fiery Traditional",  desc: "Maximum heat, full-bodied" },
    { type: "ingredients", tags: ["Garlic", "Ginger", "Extra Chilli Powder"] },
    { type: "slider",      label: "Sourness",           value: "Very High",  fill: 88, barColor: "#eab308" },
    { type: "jar",         size: "Regular Jar",         weight: "300g",      price: "₹349" },
    { type: "profile",     label: "Diabetic-Friendly",  note: "Low glycemic choices",       accent: "#2E5C5A", initial: "D" },
    { type: "slider",      label: "Oil Level",          value: "High",       fill: 75, barColor: "#f97316" },
    { type: "base",        label: "Garlic Base",        desc: "Bold, aromatic garlic",      dot: "#92400E" },
    { type: "texture",     label: "Whole Pieces",       sub: "Intact, bold bites" },
    { type: "slider",      label: "Salt Level",         value: "Medium",     fill: 45, barColor: "#f59e0b" },
    { type: "style",       label: "Oil-Rich Classic",   desc: "Deeply marinated, rich" },
    { type: "ingredients", tags: ["Mustard Oil", "Whole Chillies", "Hing"] },
    // depth 2
    { type: "slider",      label: "Spice Level",        value: "High",       fill: 82, barColor: "#dc2626" },
    { type: "profile",     label: "Kid-Friendly",       note: "Mild version available",     accent: "#D97706", initial: "K" },
    { type: "jar",         size: "Family Jar",          weight: "500g",      price: "₹499" },
    { type: "style",       label: "Andhra Style",       desc: "Bold, spicy, oil-rich" },
    { type: "ingredients", tags: ["Fenugreek", "Curry Leaves"] },
    { type: "texture",     label: "Crushed",            sub: "Rough & bold" },
    { type: "base",        label: "Mango Base",         desc: "Rich & tangy raw mango",     dot: "#D97706" },
    { type: "profile",     label: "Heart-Healthy",      note: "Low sodium, smart oils",     accent: "#9F3E18", initial: "H" },
    { type: "slider",      label: "Sourness",           value: "Medium",     fill: 55, barColor: "#eab308" },
    // depth 3
    { type: "slider",      label: "Oil Level",          value: "Medium",     fill: 50, barColor: "#f97316" },
    { type: "base",        label: "Tomato Base",        desc: "Ripe, savory tomato",        dot: "#DC2626" },
    { type: "style",       label: "Telangana Style",    desc: "Rustic, earthy, balanced" },
    { type: "ingredients", tags: ["Sesame Oil", "Hing", "Garlic"] },
    { type: "texture",     label: "Chunky",             sub: "Whole rustic pieces" },
    { type: "profile",     label: "Pregnancy-Safe",     note: "Comfort-first choices",      accent: "#C8A951", initial: "P" },
    { type: "base",        label: "Gongura Base",       desc: "Tart Andhra sorrel leaves",  dot: "#7C3AED" },
    { type: "slider",      label: "Salt Level",         value: "Low",        fill: 25, barColor: "#f59e0b" },
    { type: "style",       label: "Fiery Traditional",  desc: "Maximum heat, full-bodied" },
  ],

  // ═══ SET 2 — Wellness & Light ═════════════════════════════════════════════
  [
    // depth 1
    { type: "slider",      label: "Salt Level",         value: "Very Low",   fill: 8,  barColor: "#f59e0b" },
    { type: "profile",     label: "Pregnancy-Safe",     note: "Comfort-first choices",      accent: "#C8A951", initial: "P" },
    { type: "base",        label: "Lemon Base",         desc: "Bright, citrusy lemon",      dot: "#EAB308" },
    { type: "style",       label: "Homestyle Mild",     desc: "Gentle, comforting everyday" },
    { type: "ingredients", tags: ["Amla", "Turmeric", "Curry Leaves"] },
    { type: "slider",      label: "Spice Level",        value: "Low",        fill: 25, barColor: "#4A5D23" },
    { type: "jar",         size: "Trial Jar",           weight: "150g",      price: "₹199" },
    { type: "profile",     label: "Senior-Focused",     note: "Easy to digest, low sodium", accent: "#4A5D23", initial: "S" },
    { type: "slider",      label: "Oil Level",          value: "Very Low",   fill: 15, barColor: "#f97316" },
    { type: "base",        label: "Amla Base",          desc: "Indian gooseberry, tangy",   dot: "#65A30D" },
    { type: "texture",     label: "Soft",               sub: "Melt-in-mouth texture" },
    { type: "slider",      label: "Sourness",           value: "Medium",     fill: 50, barColor: "#eab308" },
    { type: "style",       label: "Low-Oil Modern",     desc: "Lighter, health-conscious" },
    { type: "ingredients", tags: ["Sesame Oil", "Ginger", "Fenugreek"] },
    // depth 2
    { type: "slider",      label: "Spice Level",        value: "Very Low",   fill: 15, barColor: "#4A5D23" },
    { type: "profile",     label: "Heart-Healthy",      note: "Low sodium, smart oils",     accent: "#9F3E18", initial: "H" },
    { type: "jar",         size: "Regular Jar",         weight: "300g",      price: "₹349" },
    { type: "style",       label: "Tangy & Bright",     desc: "Citrus-forward, zesty" },
    { type: "ingredients", tags: ["Lemon", "Hing", "Mustard Seeds"] },
    { type: "texture",     label: "Fine-cut",           sub: "Delicate & even slices" },
    { type: "base",        label: "Tomato Base",        desc: "Ripe, savory tomato",        dot: "#DC2626" },
    { type: "profile",     label: "Diabetic-Friendly",  note: "Low glycemic choices",       accent: "#2E5C5A", initial: "D" },
    { type: "slider",      label: "Salt Level",         value: "Low",        fill: 20, barColor: "#f59e0b" },
    // depth 3
    { type: "slider",      label: "Oil Level",          value: "Low",        fill: 28, barColor: "#f97316" },
    { type: "base",        label: "Mango Base",         desc: "Rich & tangy raw mango",     dot: "#D97706" },
    { type: "style",       label: "Andhra Style",       desc: "Bold, spicy, oil-rich" },
    { type: "ingredients", tags: ["Whole Chillies", "Jaggery Hint"] },
    { type: "texture",     label: "Chunky",             sub: "Whole rustic pieces" },
    { type: "profile",     label: "Kid-Friendly",       note: "Gentle on young palates",    accent: "#D97706", initial: "K" },
    { type: "base",        label: "Green Chilli Base",  desc: "Fresh, fiery chillies",      dot: "#16A34A" },
    { type: "slider",      label: "Sourness",           value: "High",       fill: 75, barColor: "#eab308" },
    { type: "style",       label: "Homestyle Mild",     desc: "Gentle, comforting everyday" },
  ],
];


// ─── Depth visual config ───────────────────────────────────────────────────
const DEPTH_CONFIG = {
  1: { opacity: 0.65, filter: "none",        scale: 1    },
  2: { opacity: 0.35, filter: "blur(0.6px)", scale: 0.93 },
  3: { opacity: 0.16, filter: "blur(1.5px)", scale: 0.86 },
};

// ─── Card content renderer (no positioning/animation — just the inner markup)
const CardContent = ({ card }) => {
  if (card.type === "slider") return (
    <div className="w-36">
      <div className="text-[11px] font-semibold text-sw-text mb-2">{card.label}</div>
      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${card.fill}%`, backgroundColor: card.barColor }} />
      </div>
      <div className="text-[10px] text-sw-text-muted mt-1">{card.value}</div>
    </div>
  );

  if (card.type === "base") return (
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: card.dot }} />
      <div>
        <div className="text-[11px] font-semibold text-sw-text leading-tight">{card.label}</div>
        <div className="text-[9px] text-sw-text-muted">{card.desc}</div>
      </div>
    </div>
  );

  if (card.type === "profile") return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${card.accent}22` }}>
        <span className="text-[10px] font-bold" style={{ color: card.accent }}>{card.initial}</span>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-sw-text leading-tight">{card.label}</div>
        <div className="text-[9px] text-sw-text-muted">{card.note}</div>
      </div>
    </div>
  );

  if (card.type === "style") return (
    <>
      <div className="text-[10px] font-bold text-earth-green uppercase tracking-wide mb-0.5">Style</div>
      <div className="text-[12px] font-semibold text-sw-text leading-tight">{card.label}</div>
      <div className="text-[9px] text-sw-text-muted">{card.desc}</div>
    </>
  );

  if (card.type === "ingredients") return (
    <>
      <div className="text-[9px] font-semibold text-sw-text-muted uppercase tracking-wide mb-1.5">Ingredients</div>
      <div className="flex flex-wrap gap-1">
        {card.tags.map((t) => (
          <span key={t} className="text-[10px] bg-earth-green/10 text-earth-green px-2 py-0.5 rounded-full font-medium">{t}</span>
        ))}
      </div>
    </>
  );

  if (card.type === "jar") return (
    <div className="flex items-center gap-2.5">
      <div className="w-7 h-9 rounded-md bg-earth-green/10 flex items-center justify-center shrink-0">
        <span className="text-sm">🫙</span>
      </div>
      <div>
        <div className="text-[11px] font-semibold text-sw-text leading-tight">{card.size}</div>
        <div className="text-[9px] text-sw-text-muted">{card.weight} · {card.price}</div>
      </div>
    </div>
  );

  if (card.type === "texture") return (
    <>
      <div className="text-[9px] font-semibold text-sw-text-muted uppercase tracking-wide mb-1">Texture</div>
      <div className="text-[12px] font-semibold text-sw-text">{card.label}</div>
      <div className="text-[9px] text-sw-text-muted">{card.sub}</div>
    </>
  );

  return null;
};

// ─── Floating card shell — handles position, float, entry/exit ────────────
const FloatingCard = ({ position, content, setIndex, slotIndex }) => {
  const { pos, depth, delay, dur, id } = position;
  const { opacity, filter, scale: depthScale } = DEPTH_CONFIG[depth];

  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);

  const handleDragEnd = () => {
    animate(dragX, 0, { type: "spring", stiffness: 300, damping: 28 });
    animate(dragY, 0, { type: "spring", stiffness: 300, damping: 28 });
  };

  return (
    <div className="absolute" style={{ ...pos, opacity, filter, pointerEvents: depth === 1 ? "auto" : "none" }}>
      {/* Continuous float */}
      <motion.div
        animate={{ y: [0, -(8 + (id % 3) * 4), 0], rotate: [0, id % 2 === 0 ? 1.1 : -1.1, 0] }}
        transition={{ duration: dur, repeat: Infinity, ease: "easeInOut", delay }}
        style={{ scale: depthScale, transformOrigin: "top left" }}
      >
        {/* Scroll-swap */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${setIndex}-${slotIndex}`}
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1], delay: slotIndex * 0.025 } }}
            exit={{   opacity: 0, scale: 0.92, transition: { duration: 0.28, ease: "easeIn" } }}
          >
            {/* Drag layer — x/y are separate from float so no conflicts */}
            <motion.div
              drag
              dragMomentum={false}
              dragElastic={0.08}
              style={{ x: dragX, y: dragY }}
              onDragEnd={handleDragEnd}
              whileDrag={{ scale: 1.08, boxShadow: "0 20px 40px rgba(0,0,0,0.14)", zIndex: 50, cursor: "grabbing" }}
              className="cursor-grab bg-white/80 backdrop-blur-md rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.07)] border border-white/70 p-3 select-none"
            >
              <CardContent card={content} />
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

// ─── Scroll helpers ────────────────────────────────────────────────────────
const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// ─── Main component ────────────────────────────────────────────────────────
export const HeroSection = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [setIndex, setSetIndex] = useState(0);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // Swap card set at scroll thresholds — tight so one scroll tick triggers a change
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const next = v < 0.12 ? 0 : v < 0.28 ? 1 : 2;
    setSetIndex((prev) => (prev !== next ? next : prev));
  });

  const imageY  = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const textY   = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const scale   = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  return (
    <section
      id="home"
      ref={heroRef}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-start overflow-hidden pt-24 pb-12 lg:min-h-screen lg:items-center lg:pt-20 lg:pb-0"
    >
      {/* Spice dust background accent */}
      <div className="absolute inset-x-0 top-20 h-64 bg-[radial-gradient(circle_at_top,rgba(217,119,6,0.16),transparent_72%)] pointer-events-none lg:hidden" />
      <div className="absolute top-12 right-[-8rem] h-[22rem] w-[22rem] spice-dust pointer-events-none opacity-80 lg:top-1/4 lg:right-0 lg:h-[600px] lg:w-[600px] lg:opacity-100" />
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-cream to-transparent z-10" />
      <div className="absolute left-0 top-24 h-[17rem] w-40 pointer-events-none lg:hidden" style={{ zIndex: 1 }}>
        <img
          src={`${process.env.PUBLIC_URL}/hero-img.png`}
          alt=""
          className="h-full w-full object-cover object-left opacity-70"
          style={{
            maskImage: "linear-gradient(to right, black 58%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, black 58%, transparent 100%)",
          }}
        />
      </div>

      {/* Decorative hero image — left edge */}
      <div
        className="absolute left-0 top-0 hidden h-full w-[300px] lg:block lg:w-[520px] pointer-events-none select-none"
        style={{ zIndex: 2 }}
      >
        <img
          src={`${process.env.PUBLIC_URL}/hero-img.png`}
          alt=""
          className="h-full w-full object-cover object-left"
          style={{
            maskImage: "linear-gradient(to right, black 68%, transparent 96%)",
            WebkitMaskImage: "linear-gradient(to right, black 68%, transparent 96%)",
          }}
        />
      </div>

      {/* Floating customisation cards */}
      <div className="absolute inset-0 z-0 hidden overflow-hidden lg:block">
        {[...POSITIONS]
          .sort((a, b) => b.depth - a.depth) // depth 3 first → depth 1 on top
          .map((position) => {
            const i = position.id;
            return (
              <FloatingCard
                key={position.id}
                position={position}
                content={CARD_SETS[setIndex][i]}
                setIndex={setIndex}
                slotIndex={i}
              />
            );
          })}
      </div>

      {/* Main content */}
      <motion.div
        style={{ opacity, scale }}
        className="section-container relative z-10 grid items-center gap-8 py-4 lg:grid-cols-2 lg:gap-16 lg:py-0"
      >
        {/* Left: Text */}
        <motion.div style={{ y: textY }} className="order-1 space-y-5 text-center lg:order-1 lg:space-y-8 lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="inline-flex items-center justify-center gap-2 text-earth-green text-[11px] font-medium uppercase tracking-[0.22em] sm:text-sm lg:justify-start lg:tracking-wide">
              <Sparkles size={14} />
              Personalized Indian Food
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="font-heading text-[2.65rem] font-800 leading-[0.98] text-sw-text sm:text-5xl lg:text-6xl lg:leading-[1.08]"
          >
            Craft Your Taste.{" "}
            <span className="text-earth-green">Designed for Your Health.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mx-auto max-w-md text-[15px] leading-7 text-sw-text-secondary lg:mx-0 lg:max-w-lg lg:text-lg lg:leading-relaxed"
          >
            Personalized Indian pickles made to match your taste,
            lifestyle, and wellness needs.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start"
          >
            <button
              data-testid="hero-cta-customize"
              onClick={() => navigate("/studio")}
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-earth-green px-8 py-3.5 text-sm font-medium text-white shadow-lg shadow-earth-green/20 transition-all duration-300 hover:bg-earth-green-dark hover:shadow-xl hover:shadow-earth-green/30 sm:w-auto"
            >
              Start Customizing
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToSection("products")}
              className="w-full rounded-full border border-earth-green/30 px-8 py-3.5 text-sm font-medium text-earth-green transition-all duration-300 hover:bg-earth-green hover:text-white sm:w-auto"
            >
              Explore Products
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="pt-1 text-xs text-sw-text-muted font-accent italic lg:pt-2"
          >
            One Family, One App, Perfectly Tailored Tastes.
          </motion.p>
        </motion.div>

        {/* Right: Hero image + overlays */}
        <motion.div style={{ y: imageY }} className="order-2 relative mx-auto w-full max-w-sm lg:order-2 lg:max-w-none">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-4 lg:hidden"
          >
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/80 p-3 shadow-[0_22px_60px_rgba(74,93,35,0.14)] backdrop-blur-sm">
              <div className="absolute inset-y-0 left-0 w-24 pointer-events-none">
                <img
                  src={`${process.env.PUBLIC_URL}/hero-img.png`}
                  alt=""
                  className="h-full w-full object-cover object-left opacity-55"
                  style={{
                    maskImage: "linear-gradient(to right, black 65%, transparent 100%)",
                    WebkitMaskImage: "linear-gradient(to right, black 65%, transparent 100%)",
                  }}
                />
              </div>
              <div className="relative overflow-hidden rounded-[1.6rem] bg-cream-secondary">
                <img
                  src={IMAGES.heroPickleJar}
                  alt="Premium Swadishes pickle jar"
                  data-testid="hero-image"
                  className="h-[18rem] w-full object-cover object-center"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/70 bg-white/90 px-4 py-3 text-left shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-earth-green">Heat</div>
                <div className="mt-1 text-sm font-semibold text-sw-text">Tuned to you</div>
                <div className="mt-1 text-[11px] leading-5 text-sw-text-muted">Adjust spice, oil, salt, and sweetness in minutes.</div>
              </div>
              <div className="rounded-2xl border border-white/70 bg-white/90 px-4 py-3 text-left shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-earth-green">Family</div>
                <div className="mt-1 text-sm font-semibold text-sw-text">Profiles built in</div>
                <div className="mt-1 text-[11px] leading-5 text-sw-text-muted">Create combinations for kids, elders, and wellness needs.</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:block"
          >
            <img
              src={IMAGES.heroPickleJar}
              alt="Premium Swadishes pickle jar"
              data-testid="hero-image"
              className="w-full max-w-lg mx-auto rounded-3xl"
            />

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1 }}
              className="absolute -right-2 top-1/4 hidden rounded-2xl border border-gray-100 bg-white/90 p-4 shadow-lg backdrop-blur-lg animate-float lg:block"
            >
              <div className="text-xs font-heading font-semibold text-sw-text mb-2">Spice Level</div>
              <div className="w-32 h-2 bg-cream-tertiary rounded-full overflow-hidden">
                <div className="w-3/5 h-full bg-earth-green rounded-full" />
              </div>
              <div className="text-xs text-sw-text-muted mt-1">Medium</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="absolute -left-2 bottom-1/3 hidden rounded-2xl border border-gray-100 bg-white/90 p-3 shadow-lg backdrop-blur-lg animate-float-delayed lg:block"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-earth-green/10 flex items-center justify-center">
                  <span className="text-earth-green text-xs font-bold">K</span>
                </div>
                <div>
                  <div className="text-xs font-heading font-semibold">Kid-Friendly</div>
                  <div className="text-[10px] text-sw-text-muted">Profile Active</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.4 }}
              className="absolute right-4 -bottom-4 hidden rounded-2xl border border-gray-100 bg-white/90 p-3 shadow-lg backdrop-blur-lg lg:block"
            >
              <div className="flex gap-1">
                {["Garlic", "Hing", "Fenugreek"].map((ing) => (
                  <span key={ing} className="text-[10px] bg-cream-secondary px-2 py-0.5 rounded-full text-sw-text-secondary">
                    {ing}
                  </span>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};
